// i18n.js
import { createI18n } from 'vue-i18n'

const fallbackLocale = 'he'
const localeRequests = new Map()
const apiBaseUrl = String(import.meta.env.VITE_BASE_URL || '').replace(
  /\/$/,
  ''
)
const externalLocalePath = `${apiBaseUrl}/setting/locales`
const supportedLocales = ['he', 'ar', 'en']
const localeMessages = Object.fromEntries(
  supportedLocales.map(locale => [locale, {}])
)

export { supportedLocales }

function normalizeLocale(locale) {
  return supportedLocales.includes(locale) ? locale : fallbackLocale
}

function mergeLocaleMessages(locale, messages = {}) {
  return {
    ...localeMessages[locale],
    ...messages,
  }
}

async function fetchExternalLocaleMessages(locale) {
  const response = await fetch(`${externalLocalePath}/${locale}`, {
    cache: 'no-store',
  })

  if (!response.ok) {
    throw new Error(`Failed to load locale ${locale}: ${response.status}`)
  }

  const messages = await response.json()

  if (!messages || Array.isArray(messages) || typeof messages !== 'object') {
    throw new Error(`Invalid locale payload for ${locale}`)
  }

  return messages
}

function applyLocaleToDocument(locale) {
  document.documentElement.setAttribute('lang', locale)
  document.documentElement.setAttribute(
    'dir',
    isRtlLang(locale) ? 'rtl' : 'ltr'
  )
}

export function isRtlLang(currentLocale) {
  const rtlLanguages = ['he', 'ar']
  return rtlLanguages.includes(currentLocale)
}

export function getCurrentLocale() {
  let lang = window.location.pathname.split('/')[1]
  if (!supportedLocales.includes(lang)) {
    let navigatorLang = window.navigator.language.split('-')[0]
    if (!supportedLocales.includes(navigatorLang)) {
      navigatorLang = 'en'
    }
    lang = localStorage.getItem('lang') || navigatorLang
  }
  return normalizeLocale(lang)
}

// Create i18n instance
export const i18n = createI18n({
  availableLocales: supportedLocales,
  globalInjection: true,
  locale: getCurrentLocale(),
  fallbackLocale,
  legacy: false,
  messages: localeMessages,
})

export async function ensureLocaleMessages(locale) {
  const normalizedLocale = normalizeLocale(locale)

  if (localeRequests.has(normalizedLocale)) {
    return localeRequests.get(normalizedLocale)
  }

  const request = fetchExternalLocaleMessages(normalizedLocale)
    .then(messages => {
      i18n.global.setLocaleMessage(
        normalizedLocale,
        mergeLocaleMessages(normalizedLocale, messages)
      )
    })
    .catch(error => {
      console.warn(`Using bundled translations for ${normalizedLocale}`, error)
      i18n.global.setLocaleMessage(
        normalizedLocale,
        mergeLocaleMessages(normalizedLocale)
      )
    })
    .finally(() => {
      localeRequests.delete(normalizedLocale)
    })

  localeRequests.set(normalizedLocale, request)

  return request
}

export async function initializeI18n() {
  const locale = getCurrentLocale()

  await ensureLocaleMessages(locale)
  applyLocaleToDocument(locale)
  i18n.global.locale.value = locale
}

export async function setLocale(locale) {
  const normalizedLocale = normalizeLocale(locale)

  await ensureLocaleMessages(normalizedLocale)
  applyLocaleToDocument(normalizedLocale)
  localStorage.setItem('lang', normalizedLocale)
  i18n.global.locale.value = normalizedLocale

  return normalizedLocale
}

// Export the i18n instance
export default i18n
